import { Profile, User } from '../types/models';

/**
 * プロファイル権限のキー（3階層: section.group.flag）
 * section のみの場合は view.canView に相当するショートカット
 */
export type PermissionKey =
  // section-level shortcuts (view.canView に相当)
  | 'userMgmt'
  | 'scenarioMgmt'
  | 'analytics'
  | 'profileMgmt'
  // userMgmt
  | 'userMgmt.view.canView'
  | 'userMgmt.view.canCsvDownload'
  | 'userMgmt.edit.canCreateNew'
  | 'userMgmt.edit.canCsvImport'
  | 'userMgmt.edit.canBulkUpdate'
  | 'userMgmt.edit.canUpdate'
  | 'userMgmt.edit.canDelete'
  | 'userMgmt.edit.canChangePassword'
  // scenarioMgmt
  | 'scenarioMgmt.view.canView'
  | 'scenarioMgmt.view.canCsvDownload'
  | 'scenarioMgmt.edit.canCreateNew'
  | 'scenarioMgmt.edit.canCsvImport'
  | 'scenarioMgmt.edit.canUpdate'
  | 'scenarioMgmt.edit.canDelete'
  | 'scenarioMgmt.edit.canCustomScenarioUpdate'
  // analytics
  | 'analytics.view.canView'
  | 'analytics.view.canCsvDownload'
  // profileMgmt
  | 'profileMgmt.view.canView'
  | 'profileMgmt.edit.canCreateNew'
  | 'profileMgmt.edit.canUpdate'
  | 'profileMgmt.edit.canDelete';

export function isSystemAdmin(user: User | null | undefined): boolean {
  return !!user && user.userRole === 'SYSTEM_ADMIN';
}

export function isAdmin(user: User | null | undefined): boolean {
  return !!user && (user.userRole === 'ADMIN' || user.userRole === 'SYSTEM_ADMIN');
}

/**
 * 管理系メニューの可否を判定。
 * - SYSTEM_ADMIN は常に true
 * - ADMIN は profile.permissions に基づく
 * - USER は常に false
 */
export function canAccess(user: User | null | undefined, key: PermissionKey): boolean {
  if (!user) return false;
  if (isSystemAdmin(user)) return true;
  if (!isAdmin(user)) return false;

  const profile = user.profile;
  // プロファイル未割り当てでも、既存の「locked=true」特権管理者は全機能を許可
  if (!profile) return !!user.locked;

  const perms = profile.permissions as any;
  const parts = key.split('.');

  if (parts.length === 1) {
    // section-level: view.canView を確認
    return !!perms[parts[0]]?.view?.canView;
  }

  if (parts.length === 3) {
    const [section, group, flag] = parts;
    return !!perms[section]?.[group]?.[flag];
  }

  return false;
}

/**
 * プロファイル一覧から会社の全権プロファイルを取得する（SYSTEM_ADMIN 用の補助関数）
 */
export function getFullAccessProfile(profiles: Profile[] | null | undefined): Profile | undefined {
  if (!profiles) return undefined;
  return profiles.find(p => {
    const u = p.permissions.userMgmt;
    const s = p.permissions.scenarioMgmt;
    const a = p.permissions.analytics;
    const pr = p.permissions.profileMgmt;
    return (
      u.view.canView && u.view.canCsvDownload &&
      u.edit.canCreateNew && u.edit.canCsvImport && u.edit.canBulkUpdate &&
      u.edit.canUpdate && u.edit.canDelete && u.edit.canChangePassword &&
      s.view.canView && s.view.canCsvDownload &&
      s.edit.canCreateNew && s.edit.canCsvImport && s.edit.canUpdate && s.edit.canDelete &&
      a.view.canView && a.view.canCsvDownload &&
      pr.view.canView &&
      pr.edit.canCreateNew && pr.edit.canUpdate && pr.edit.canDelete
    );
  });
}
